import { RoleSelector } from "@bcc/components/finance/role-selector";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@bcc/components/ui/card";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@bcc/components/ui/select";
import { Separator } from "@bcc/components/ui/separator";
import { Switch } from "@bcc/components/ui/switch";
import { useAuth } from "@bcc/contexts/auth-context";
import { useToast } from "@bcc/hooks/use-toast";
import { AlertCircle, CheckCircle, Lock, Save } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  setFinanceSettingsViewDateFormatLabelTestId,
  setFinanceSettingsViewDefaultCountryLabelTestId,
  setFinanceSettingsViewDefaultDateRangeLabelTestId,
  setFinanceSettingsViewDefaultsCardDescriptionTestId,
  setFinanceSettingsViewDefaultsCardTestId,
  setFinanceSettingsViewDefaultsCardTitleTestId,
  setFinanceSettingsViewDefaultStatusLabelTestId,
  setFinanceSettingsViewExportCardDescriptionTestId,
  setFinanceSettingsViewExportCardTestId,
  setFinanceSettingsViewExportCardTitleTestId,
  setFinanceSettingsViewExportFormatLabelTestId,
  setFinanceSettingsViewGermanyVatSectionTestId,
  setFinanceSettingsViewIncludeInExportsTestId,
  setFinanceSettingsViewLineItemsOptionTestId,
  setFinanceSettingsViewNetherlandsVatSectionTestId,
  setFinanceSettingsViewNumberFormatLabelTestId,
  setFinanceSettingsViewPhase1BadgeTestId,
  setFinanceSettingsViewPhase1RowTestId,
  setFinanceSettingsViewPhase2BadgeTestId,
  setFinanceSettingsViewPhase2RowTestId,
  setFinanceSettingsViewPhasesCardDescriptionTestId,
  setFinanceSettingsViewPhasesCardTestId,
  setFinanceSettingsViewPhasesCardTitleTestId,
  setFinanceSettingsViewSaveButtonTestId,
  setFinanceSettingsViewSubtitleTestId,
  setFinanceSettingsViewTaxDetailsOptionTestId,
  setFinanceSettingsViewTestId,
  setFinanceSettingsViewTitleTestId,
  setFinanceSettingsViewVatCardDescriptionTestId,
  setFinanceSettingsViewVatCardTestId,
  setFinanceSettingsViewVatCardTitleTestId,
} from "./finance-settings-view.utils";

interface InitialSettings {
  tax: {
    defaultCountry: "DE" | "NL";
    deVatId: string;
    nlVatId: string;
    deValidationEnabled: boolean;
    nlValidationEnabled: boolean;
  };
  export: {
    exportFormat: "csv" | "pdf";
    includeTaxDetails: boolean;
    includeLineItems: boolean;
    dateFormat: "EU" | "US";
    numberFormat: "EU" | "US";
  };
  defaults: {
    defaultDateRange: string;
    defaultStatus: string;
  };
  phases: {
    phase1Enabled: boolean;
    phase2Enabled: boolean;
  };
}

interface FinanceSettingsViewProps {
  initialSettings: InitialSettings;
  testId?: string;
}

// Subcomponents
interface VATValidationSectionProps {
  country: string;
  label: string;
  formatHint: string;
  vatId: string;
  onVatIdChange: (value: string) => void;
  validationEnabled: boolean;
  onValidationChange: (value: boolean) => void;
  isValid: boolean;
  canModify: boolean;
  validationPattern: string;
  testId?: string;
}

function VATValidationSection({
  country,
  label,
  formatHint,
  vatId,
  onVatIdChange,
  validationEnabled,
  onValidationChange,
  isValid,
  canModify,
  validationPattern,
  testId,
}: VATValidationSectionProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">{label}</h3>
          <p className="text-xs text-muted-foreground">{formatHint}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t("views.financeSettings.validation")}
          </span>
          <Switch
            checked={validationEnabled}
            onCheckedChange={onValidationChange}
            disabled={!canModify}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${country}-vat`}>
          {t("views.financeSettings.vatId")}
        </Label>
        <div className="flex gap-2">
          <Input
            id={`${country}-vat`}
            value={vatId}
            onChange={(e) => onVatIdChange(e.target.value)}
            placeholder={validationPattern}
            disabled={!canModify}
            className={vatId && !isValid ? "border-red-500" : ""}
          />
          {vatId && (
            <div className="flex items-center">
              {isValid ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
            </div>
          )}
        </div>
        {vatId && !isValid && (
          <p className="text-xs text-red-600">
            {t("views.financeSettings.invalidFormat", { hint: formatHint })}
          </p>
        )}
      </div>
    </div>
  );
}

interface ExportOptionProps {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  disabled: boolean;
  testId?: string;
}

function ExportOption({
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  testId,
}: ExportOptionProps) {
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <div className="space-y-0.5">
        <Label>{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  );
}

interface PhaseConfigRowProps {
  label: string;
  badge: React.ReactNode;
  description: string;
  enabled: boolean;
  onEnabledChange: (value: boolean) => void;
  disabled: boolean;
  testId?: string;
  badgeTestId?: string;
}

function PhaseConfigRow({
  label,
  badge,
  description,
  enabled,
  onEnabledChange,
  disabled,
  testId,
  badgeTestId,
}: PhaseConfigRowProps) {
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <Label>{label}</Label>
          <span data-testid={badgeTestId}>{badge}</span>
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch
        checked={enabled}
        onCheckedChange={onEnabledChange}
        disabled={disabled}
      />
    </div>
  );
}

export function FinanceSettingsView({
  initialSettings,
  testId,
}: FinanceSettingsViewProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { roaster } = useAuth();
  // Mock finance role since auth context might not have it
  const userFinanceRole = "finance-ops";
  const canModifySettings = userFinanceRole === "finance-ops";

  // Tax Settings
  const [defaultCountry, setDefaultCountry] = useState<"DE" | "NL">(
    initialSettings.tax.defaultCountry,
  );
  const [deVatId, setDeVatId] = useState(initialSettings.tax.deVatId);
  const [nlVatId, setNlVatId] = useState(initialSettings.tax.nlVatId);
  const [deValidationEnabled, setDeValidationEnabled] = useState(
    initialSettings.tax.deValidationEnabled,
  );
  const [nlValidationEnabled, setNlValidationEnabled] = useState(
    initialSettings.tax.nlValidationEnabled,
  );

  // Export Settings
  const [exportFormat, setExportFormat] = useState<"csv" | "pdf">(
    initialSettings.export.exportFormat,
  );
  const [includeTaxDetails, setIncludeTaxDetails] = useState(
    initialSettings.export.includeTaxDetails,
  );
  const [includeLineItems, setIncludeLineItems] = useState(
    initialSettings.export.includeLineItems,
  );
  const [dateFormat, setDateFormat] = useState<"EU" | "US">(
    initialSettings.export.dateFormat,
  );
  const [numberFormat, setNumberFormat] = useState<"EU" | "US">(
    initialSettings.export.numberFormat,
  );

  // Default Settings
  const [defaultDateRange, setDefaultDateRange] = useState(
    initialSettings.defaults.defaultDateRange,
  );
  const [defaultStatus, setDefaultStatus] = useState(
    initialSettings.defaults.defaultStatus,
  );

  // Phase Flags
  const [phase1Enabled, setPhase1Enabled] = useState(
    initialSettings.phases.phase1Enabled,
  );
  const [phase2Enabled, setPhase2Enabled] = useState(
    initialSettings.phases.phase2Enabled,
  );

  // VAT Validation
  const deVatValid = /^DE[0-9]{9}$/.test(deVatId);
  const nlVatValid = /^NL[0-9]{9}B[0-9]{2}$/.test(nlVatId);

  const handleSaveSettings = () => {
    if (!canModifySettings) {
      toast({
        title: t("views.financeSettings.permissionDenied"),
        description: t("views.financeSettings.onlyFinanceOps"),
        variant: "destructive",
      });
      return;
    }

    const settings = {
      tax: {
        defaultCountry,
        vatIds: {
          DE: deVatId,
          NL: nlVatId,
        },
        validation: {
          DE: deValidationEnabled,
          NL: nlValidationEnabled,
        },
      },
      export: {
        defaultFormat: exportFormat,
        includeTaxDetails,
        includeLineItems,
        dateFormat,
        numberFormat,
      },
      defaults: {
        dateRange: defaultDateRange,
        status: defaultStatus,
        country: defaultCountry,
      },
      phases: {
        phase1: phase1Enabled,
        phase2: phase2Enabled,
      },
    };

    localStorage.setItem("finance-settings", JSON.stringify(settings));

    toast({
      title: t("views.financeSettings.settingsSaved"),
      description: t("views.financeSettings.settingsUpdated"),
    });
  };

  return (
    <div
      data-testid={testId ?? setFinanceSettingsViewTestId("container")}
      className="space-y-6 max-w-4xl"
    >
      {/* Header */}
      <div>
        <h1
          className="text-2xl font-semibold tracking-tight text-balance"
          data-testid={setFinanceSettingsViewTitleTestId()}
        >
          {t("views.financeSettings.title")}
        </h1>
        <p
          className="mt-1 text-sm text-muted-foreground"
          data-testid={setFinanceSettingsViewSubtitleTestId()}
        >
          {t("views.financeSettings.description")}
        </p>
      </div>

      {!canModifySettings && (
        <div className="rounded-lg bg-yellow-50 dark:bg-yellow-950 p-4 border border-yellow-200 dark:border-yellow-800">
          <div className="flex gap-2">
            <Lock className="h-5 w-5 text-yellow-600 dark:text-yellow-500 shrink-0" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-200">
                {t("views.financeSettings.readOnlyAccess")}
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                {t("views.financeSettings.readOnlyMessage")}
              </p>
            </div>
          </div>
        </div>
      )}

      <RoleSelector />

      {/* Country VAT Validation Card */}
      <Card data-testid={setFinanceSettingsViewVatCardTestId()}>
        <CardHeader>
          <CardTitle data-testid={setFinanceSettingsViewVatCardTitleTestId()}>
            {t("views.financeSettings.vatValidation.title")}
          </CardTitle>
          <CardDescription
            data-testid={setFinanceSettingsViewVatCardDescriptionTestId()}
          >
            {t("views.financeSettings.vatValidation.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <VATValidationSection
            country="de"
            label={t("views.financeSettings.vatValidation.germany")}
            formatHint={t("views.financeSettings.vatValidation.deFormat")}
            vatId={deVatId}
            onVatIdChange={setDeVatId}
            validationEnabled={deValidationEnabled}
            onValidationChange={setDeValidationEnabled}
            isValid={deVatValid}
            canModify={canModifySettings}
            validationPattern="DE123456789"
            testId={setFinanceSettingsViewGermanyVatSectionTestId()}
          />

          <Separator />

          <VATValidationSection
            country="nl"
            label={t("views.financeSettings.vatValidation.netherlands")}
            formatHint={t("views.financeSettings.vatValidation.nlFormat")}
            vatId={nlVatId}
            onVatIdChange={setNlVatId}
            validationEnabled={nlValidationEnabled}
            onValidationChange={setNlValidationEnabled}
            isValid={nlVatValid}
            canModify={canModifySettings}
            validationPattern="NL123456789B01"
            testId={setFinanceSettingsViewNetherlandsVatSectionTestId()}
          />
        </CardContent>
      </Card>

      {/* Export Templates Card */}
      <Card data-testid={setFinanceSettingsViewExportCardTestId()}>
        <CardHeader>
          <CardTitle
            data-testid={setFinanceSettingsViewExportCardTitleTestId()}
          >
            {t("views.financeSettings.exportTemplates.title")}
          </CardTitle>
          <CardDescription
            data-testid={setFinanceSettingsViewExportCardDescriptionTestId()}
          >
            {t("views.financeSettings.exportTemplates.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label
              htmlFor="export-format"
              data-testid={setFinanceSettingsViewExportFormatLabelTestId()}
            >
              {t("views.financeSettings.exportTemplates.defaultFormat")}
            </Label>
            <Select
              value={exportFormat}
              onValueChange={(value: "csv" | "pdf") => setExportFormat(value)}
              disabled={!canModifySettings}
            >
              <SelectTrigger id="export-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">
                  {t("views.financeSettings.exportTemplates.csv")}
                </SelectItem>
                <SelectItem value="pdf">
                  {t("views.financeSettings.exportTemplates.pdf")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label
                htmlFor="date-format"
                data-testid={setFinanceSettingsViewDateFormatLabelTestId()}
              >
                {t("views.financeSettings.exportTemplates.dateFormat")}
              </Label>
              <Select
                value={dateFormat}
                onValueChange={(value: "EU" | "US") => setDateFormat(value)}
                disabled={!canModifySettings}
              >
                <SelectTrigger id="date-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EU">
                    {t("views.financeSettings.exportTemplates.dateEu")}
                  </SelectItem>
                  <SelectItem value="US">
                    {t("views.financeSettings.exportTemplates.dateUs")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="number-format"
                data-testid={setFinanceSettingsViewNumberFormatLabelTestId()}
              >
                {t("views.financeSettings.exportTemplates.numberFormat")}
              </Label>
              <Select
                value={numberFormat}
                onValueChange={(value: "EU" | "US") => setNumberFormat(value)}
                disabled={!canModifySettings}
              >
                <SelectTrigger id="number-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EU">
                    {t("views.financeSettings.exportTemplates.numberEu")}
                  </SelectItem>
                  <SelectItem value="US">
                    {t("views.financeSettings.exportTemplates.numberUs")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <h3
              className="text-sm font-semibold"
              data-testid={setFinanceSettingsViewIncludeInExportsTestId()}
            >
              {t("views.financeSettings.exportTemplates.includeInExports")}
            </h3>

            <ExportOption
              label={t("views.financeSettings.exportTemplates.taxDetails")}
              description={t(
                "views.financeSettings.exportTemplates.taxDetailsDesc",
              )}
              checked={includeTaxDetails}
              onCheckedChange={setIncludeTaxDetails}
              disabled={!canModifySettings}
              testId={setFinanceSettingsViewTaxDetailsOptionTestId()}
            />

            <ExportOption
              label={t("views.financeSettings.exportTemplates.lineItems")}
              description={t(
                "views.financeSettings.exportTemplates.lineItemsDesc",
              )}
              checked={includeLineItems}
              onCheckedChange={setIncludeLineItems}
              disabled={!canModifySettings}
              testId={setFinanceSettingsViewLineItemsOptionTestId()}
            />
          </div>

          {canModifySettings && (
            <div className="rounded-lg bg-blue-50 dark:bg-blue-950 p-4 border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {t("views.financeSettings.exportTemplates.previewHint")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Defaults Card */}
      <Card data-testid={setFinanceSettingsViewDefaultsCardTestId()}>
        <CardHeader>
          <CardTitle
            data-testid={setFinanceSettingsViewDefaultsCardTitleTestId()}
          >
            {t("views.financeSettings.defaults.title")}
          </CardTitle>
          <CardDescription
            data-testid={setFinanceSettingsViewDefaultsCardDescriptionTestId()}
          >
            {t("views.financeSettings.defaults.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label
              htmlFor="default-country"
              data-testid={setFinanceSettingsViewDefaultCountryLabelTestId()}
            >
              {t("views.financeSettings.defaults.defaultCountry")}
            </Label>
            <Select
              value={defaultCountry}
              onValueChange={(value: "DE" | "NL") => setDefaultCountry(value)}
              disabled={!canModifySettings}
            >
              <SelectTrigger id="default-country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="DE">
                  {t("views.financeSettings.defaults.countryOptions.germany")}
                </SelectItem>
                <SelectItem value="NL">
                  {t(
                    "views.financeSettings.defaults.countryOptions.netherlands",
                  )}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="default-date-range"
              data-testid={setFinanceSettingsViewDefaultDateRangeLabelTestId()}
            >
              {t("views.financeSettings.defaults.defaultDateRange")}
            </Label>
            <Select
              value={defaultDateRange}
              onValueChange={setDefaultDateRange}
              disabled={!canModifySettings}
            >
              <SelectTrigger id="default-date-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this-month">
                  {t(
                    "views.financeSettings.defaults.dateRangeOptions.thisMonth",
                  )}
                </SelectItem>
                <SelectItem value="last-month">
                  {t(
                    "views.financeSettings.defaults.dateRangeOptions.lastMonth",
                  )}
                </SelectItem>
                <SelectItem value="this-quarter">
                  {t(
                    "views.financeSettings.defaults.dateRangeOptions.thisQuarter",
                  )}
                </SelectItem>
                <SelectItem value="this-year">
                  {t(
                    "views.financeSettings.defaults.dateRangeOptions.thisYear",
                  )}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="default-status"
              data-testid={setFinanceSettingsViewDefaultStatusLabelTestId()}
            >
              {t("views.financeSettings.defaults.defaultStatusFilter")}
            </Label>
            <Select
              value={defaultStatus}
              onValueChange={setDefaultStatus}
              disabled={!canModifySettings}
            >
              <SelectTrigger id="default-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t(
                    "views.financeSettings.defaults.statusOptions.allStatuses",
                  )}
                </SelectItem>
                <SelectItem value="paid">
                  {t("views.financeSettings.defaults.statusOptions.paidOnly")}
                </SelectItem>
                <SelectItem value="unpaid">
                  {t("views.financeSettings.defaults.statusOptions.unpaidOnly")}
                </SelectItem>
                <SelectItem value="overdue">
                  {t(
                    "views.financeSettings.defaults.statusOptions.overdueOnly",
                  )}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Phase Flags Card */}
      <Card data-testid={setFinanceSettingsViewPhasesCardTestId()}>
        <CardHeader>
          <CardTitle
            data-testid={setFinanceSettingsViewPhasesCardTitleTestId()}
          >
            {t("views.financeSettings.phases.title")}
          </CardTitle>
          <CardDescription
            data-testid={setFinanceSettingsViewPhasesCardDescriptionTestId()}
          >
            {t("views.financeSettings.phases.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <PhaseConfigRow
            label={t("views.financeSettings.phases.phase1.label")}
            badge={
              <Badge variant="default">
                {t("views.financeSettings.phases.badges.active")}
              </Badge>
            }
            description={t("views.financeSettings.phases.phase1.description")}
            enabled={phase1Enabled}
            onEnabledChange={setPhase1Enabled}
            disabled={!canModifySettings}
            testId={setFinanceSettingsViewPhase1RowTestId()}
            badgeTestId={setFinanceSettingsViewPhase1BadgeTestId()}
          />

          <Separator />

          <PhaseConfigRow
            label={t("views.financeSettings.phases.phase2.label")}
            badge={
              <Badge variant="outline">
                {t("views.financeSettings.phases.badges.comingSoon")}
              </Badge>
            }
            description={t("views.financeSettings.phases.phase2.description")}
            enabled={phase2Enabled}
            onEnabledChange={setPhase2Enabled}
            disabled={true}
            testId={setFinanceSettingsViewPhase2RowTestId()}
            badgeTestId={setFinanceSettingsViewPhase2BadgeTestId()}
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveSettings}
          disabled={!canModifySettings}
          className="gap-2"
          data-testid={setFinanceSettingsViewSaveButtonTestId()}
        >
          <Save className="h-4 w-4" />
          {t("views.financeSettings.saveSettings")}
        </Button>
      </div>
    </div>
  );
}
